<template>
  <div @click="emit('onPlanSelected', product.id)" class="group relative my-2 lg:my-0 rounded-2xl flex flex-col bg-opacity-70 transition ease-in-out duration-100 hover:rounded-2xl hover:bg-gradient-to-tr hover:from-brand-primary-light hover:to-brand-primary-dark hover:p-1 " :class="{ 'bg-opacity-100': !isSelectedProduct, 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark p-1': isSelectedProduct, 'cursor-pointer': canSelectProduct }">
    <div class="transition ease-in-out group-hover:bg-white p-8 rounded-xl border-black border-solid border hover:border-none" :class="[isSelectedProduct? 'bg-white border-none' : 'bg-opacity-70 bg-white']">
      <div class="absolute h-40 right-10 inset-y-12">
        <img class="absolute right-0 opacity-100 group-hover:opacity-0 transition-opacity ease-in-out duration-300" :src=product.img />
        <img class="absolute right-0 opacity-0 group-hover:opacity-100 transition-opacity ease-in-out duration-300" :src=product.imghover />
      </div>
      <div class="flex-1 group-hover:pt-2" :class="[isSelectedProduct? 'pt-2' : 'pt-0']">
        <h3 class="text-lg font-poppins text-black">{{ product.name }}</h3>
        <!-- <p v-if="product.download === 250" class="absolute top-0 py-1.5 px-4 bg-brand-primary rounded-full text-xs font-semibold tracking-wide text-brand-button-text transform -translate-y-1/2">Most Popular!</p> -->

        <div class="mt-0 lg:mt-4">
          <div class="flex items-center font-poppins-bold">
            <div class="leading-7">
              <span :class="['tracking-wide font-poppins-bold', product.displayDiscount ? 'text-2xl text-gray-300 text-decoration-line: line-through' : 'text-4xl font-poppins-bold']">${{ product.price }}</span>
              <p v-if="product.displayDiscount" class="mb-1 tracking-wide font-poppins-bold text-4xl">${{ product.discountedPrice }}</p>
            </div>
            <div class="-mt-2 ml-1 text-md font-poppins">/month</div>
          </div>
        </div>

        <p v-if="product.typicalEveningSpeed" class="mt-6 mb-2 text-black font-poppins"><span class="font-poppins text-black">{{ product.typicalEveningSpeed }} Mbps</span> typical evening speed</p>
        <p v-else-if="product.typicalEveningSpeed === 0 || !product.typicalEveningSpeed" class="mt-6 mb-2 text-black font-poppins text-xs">Average sampled evening speed not currently available</p>
        <!-- Feature list -->
        <ul class="mt-2 mb-6 space-y-2 block font-poppins text-sm disc list-inside h-28">
          <li class="text-sm">
            <p class="ml-3 text-black">{{ product.description }}</p>
          </li>

          <li v-for="standardFeature in standardFeatures" :key="standardFeature" class="text-sm">
            <p class="ml-3 text-black">{{ standardFeature }}</p>
          </li>
          <li class="text-sm">
            <p class="ml-3 text-black">{{ product.download }}/{{ product.upload }}  (Mbps download/upload)</p>
          </li>
        </ul>
      </div>
      <div v-if="!canSelectProduct" class="max-w-48 flex rounded-full bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark p-0.5 shadow-md mb-4">
          <button class="flex-1 text-18 bg-white group-hover:bg-transparent px-6 py-3 rounded-full font-poppins border-none hover:cursor-pointer" onclick="javascript:document.getElementById('primary-address-search').focus()">
              Check Your Address
          </button>
      </div>

      <div v-if="canSelectProduct" class="max-w-48 flex rounded-full bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark p-0.5 shadow-md mb-4">
          <button class="cursor-pointer flex-1 text-18 group-hover:bg-transparent px-6 py-3 rounded-full font-poppins border-none" :class="[isSelectedProduct? 'bg-transparent' : 'bg-white']">
              {{ isSelectedProduct ? 'Selected' : 'Select Plan'}}
          </button>
      </div>
      <!-- Links -->
      <a v-for="standardLink in standardLinks" :key="standardLink.name" :href="standardLink.link" target="_blank" class="block href text-black hover:text-brand-primary hover:underline transition duration-400 my-1 font-poppins-bold text-18 group-hover:pb-2 pt-1 ">
        {{ standardLink.name }}
      </a>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { CheckIcon } from '@heroicons/vue/outline';
import { defineProps, defineEmits, computed, ref } from 'vue';
import { BrandServiceInterface, PlanSelectorProductInformation } from '@/services/brand/brandServiceInterface';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';

const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');

type Product = {
  id: string,
  name: string,
  description: string,
  download: number | null,
  upload: number | null,
  typicalEveningSpeed: number | null,
  price: number,
  discountedPrice?: number,
  discounted?: boolean,
  displayDiscount?: boolean,
  img?: string,
  imghover?: string
}

const props = defineProps<{
  canSelectProduct: boolean,
  isSelectedProduct: boolean,
  product: Product,
  planSelectorProductInformation: PlanSelectorProductInformation
}>();

const emit = defineEmits<{(event: 'onPlanSelected', id: string): void}>();

const mostPopular = computed(() => {
  return props.product.download === 250
    ? 'text-white hover:bg-brand-primary'
    : 'text-brand-secondary hover:bg-brand-primary';
});

const standardFeatures = ref(props.planSelectorProductInformation.standardFeatures).value;
const standardLinks = ref(props.planSelectorProductInformation.standardLinks).value;

</script>

<style scoped>
.disc {
  list-style-type: disc !important;
  list-style: disc;
}
</style>
